import { NgModule, provideZonelessChangeDetection } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FSABPreloadingStrategy } from './app.preload-strategy';
import { appRoutes } from './routes';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import {
  MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor,
  MsalInterceptorConfiguration, MsalModule, MsalRedirectComponent, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { HomeComponent } from './home/home.component';
import { FAB_THEME_SETTING } from '@fabric/components/theme';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ErrorHandlerInterceptor } from './core/interceptors/error-handler.interceptor';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: environment.auth,
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false
      },
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('/api/', [environment.auth.apiScope]);
  protectedResourceMap.set('https://graph.microsoft.com/', ['User.Read.All']);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
  };
}

@NgModule(
  {
    declarations: [
        AppComponent,
        HomeComponent,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
    imports: [
      SharedModule,
      CoreModule,
      BrowserModule,
      AppRoutingModule,
      MsalModule,
      RouterModule.forRoot(appRoutes, {
          preloadingStrategy: FSABPreloadingStrategy,
          anchorScrolling: 'enabled',
          onSameUrlNavigation: 'reload', scrollPositionRestoration: 'enabled'
      })
    ],
    providers: [
      FSABPreloadingStrategy,
      Title,
      { provide: 'ORIGIN_URL', useValue: location.origin },
      { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },
      { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
      { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
      { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'always', subscriptSizing: 'dynamic' } },
      { provide: FAB_THEME_SETTING, useValue: 'light' },
      provideHttpClient(withInterceptorsFromDi()),
      provideZonelessChangeDetection(),
    ]
  })
export class AppModule { }
